/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useParams } from 'react-router';

import { formatDateTime, MISSING } from 'common/helpers/format';
import { useActions } from 'common/hooks/useActions';
import { useTypedSelector } from 'common/hooks/useTypedSelector';
import DetailGrid, { DetailItem } from 'components/detailGrid';
import PanelBoxLayout from 'components/layout/panelBox';
import PageTitle from 'components/pageTitle';
import StatusChip from 'components/statusChip';
import Thumb from 'components/thumb';
import Value from 'components/value';

import { CALL_THE_WAITER, CALL_THE_WAITER_TEXT, WaiterIcon } from '../callTheWaiter';
import * as S from './styles';

/**
 * The menu category decides the thumbnail's ground, so a cart of four things
 * reads as food-and-two-hot-drinks before a word of it is read. The API's
 * category values are `food` / `cold_drink` / `hot_drink`; the design's three
 * grounds are `food` / `cold` / `hot`.
 */
const TONES: Record<string, 'food' | 'cold' | 'hot'> = {
  food: 'food',
  cold_drink: 'cold',
  hot_drink: 'hot'
};

/**
 * What one option was set to.
 *
 * A choice-type option carries its selections in `optionItemValues`; a yes/no
 * option carries `booleanValue` instead. **No is a value, not an absence** —
 * "no sugar" is an instruction to the tea boy and has to survive to the
 * screen, which is why a false boolean renders "No" rather than being dropped.
 *
 * `optionItemValues` is an array and the backend's multi-select types can put
 * more than one entry in it; reading `[0]` dropped every choice after the
 * first, so an order for milk *and* sugar reached the tea boy as milk.
 */
const optionValue = (option: any): string => {
  const chosen: string[] = (option?.optionItemValues || [])
    .map((value: any) => value?.title)
    .filter((title: string) => !!title);
  if (chosen.length) return chosen.join(', ');
  return option?.booleanValue ? 'Yes' : 'No';
};

const CafeteriaOrderDetail = () => {
  const { ordersListDetailRequestAction } = useActions();
  const { order }: any = useTypedSelector((state) => state.Cafeteria);
  const params: any = useParams();

  const data = order?.data?.list;
  const status: string = data?.status;
  const isCancelled = status === 'cancelled_by_user' || status === 'cancelled_by_teaboy';

  /* `cart: null` is a Call the Waiter request, not a cart that failed to load. */
  const isCall = data?.cart == null;
  const items: any[] = data?.cart?.items || [];

  useEffect(() => {
    if (params?.id) ordersListDetailRequestAction(params?.id);
  }, [params]);

  return (
    <S.Container>
      <PageTitle title="Order Details" />

      <PanelBoxLayout
        className="flush"
        contentStyle={{ padding: 0 }}
        title={data?.id ? `Order #${data.id}` : 'Order'}
        afterTitle={<StatusChip domain="order" value={status} fallback={<Value />} />}
      >
        <DetailGrid>
          <DetailItem label="Employee">
            <S.Person>
              {/*
               * Thumb, not an <img> with a default-avatar fallback: a profile
               * picture is an imgproxy URL like every other image in the
               * panel, and every one of them 404s, so the fallback never ran
               * and the cell drew the browser's broken-image glyph.
               */}
              <Thumb src={data?.user?.profilePicture} name={data?.user?.displayName} />
              <S.PersonName>
                <Value fallback={MISSING.provided}>{data?.user?.displayName}</Value>
              </S.PersonName>
            </S.Person>
          </DetailItem>

          <DetailItem label="Floor">
            <Value fallback={MISSING.none}>{data?.floor?.name}</Value>
          </DetailItem>

          <DetailItem label="Location">
            <Value fallback={MISSING.provided}>{data?.location}</Value>
          </DetailItem>

          <DetailItem label="Ordered">
            <Value mono>{formatDateTime(data?.localDate)}</Value>
          </DetailItem>

          <DetailItem label="Tea boy">
            <Value fallback={MISSING.assigned}>{data?.teaBoy?.user?.displayName}</Value>
          </DetailItem>

          <DetailItem label="Tea boy email">
            <Value fallback={MISSING.provided}>{data?.teaBoy?.mail}</Value>
          </DetailItem>

          <DetailItem label="Note" wide prose>
            <Value fallback={MISSING.provided}>{data?.description}</Value>
          </DetailItem>

          {/*
           * The cancellation reason used to be reachable only by hovering an
           * info icon inside the red status box — the one piece of writing a
           * person left about this order, hidden behind a hover, on the one
           * screen that exists to explain the order. It is a field.
           */}
          {isCancelled ? (
            <DetailItem label="Cancel reason" wide prose>
              <Value fallback={MISSING.provided}>{data?.cancelReasonText}</Value>
            </DetailItem>
          ) : (
            <></>
          )}
        </DetailGrid>
      </PanelBoxLayout>

      {isCall ? (
        <PanelBoxLayout title="Request">
          <S.Waiter>
            <S.WaiterIcon>
              <WaiterIcon />
            </S.WaiterIcon>
            <div>
              <S.WaiterTitle>{CALL_THE_WAITER}</S.WaiterTitle>
              <S.WaiterText>{CALL_THE_WAITER_TEXT}</S.WaiterText>
            </div>
          </S.Waiter>
        </PanelBoxLayout>
      ) : (
        <PanelBoxLayout
          className="flush"
          contentStyle={{ padding: 0 }}
          title="Cart"
          hint={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
        >
          {/*
           * One line per item; under it, one row per configuration.
           *
           * The shape of the payload is the reason this is not a flat list.
           * `item.quantity` is how many of that food were ordered, and
           * `optionGroups` splits that total into the ways they were asked
           * for: two coffees with sugar and one without is one item of
           * quantity three carrying two groups of quantity two and one. The
           * old screen printed the options as a comma-joined
           * `Title: Value` string and put the group's count in a teal circle
           * at the far right of the card, so which options belonged to which
           * of the three coffees was not recoverable from the screen.
           */}
          <S.Lines>
            {items.map((item: any, index: number) => {
              const groups: any[] = item?.optionGroups || [];

              return (
                <S.Line key={item?.id ?? index}>
                  <S.LineHead>
                    <Thumb
                      src={item?.food?.image}
                      name={item?.food?.title}
                      tone={TONES[item?.food?.category] || 'neutral'}
                    />
                    <S.LineTitle>
                      <Value fallback={MISSING.provided}>{item?.food?.title}</Value>
                    </S.LineTitle>
                    <S.Qty>{`× ${item?.quantity ?? 1}`}</S.Qty>
                  </S.LineHead>

                  {groups.length > 0 ? (
                    <S.Groups>
                      {groups.map((group: any, groupIndex: number) => (
                        <S.Group key={groupIndex}>
                          {/*
                           * Only worth saying when the item was asked for in
                           * more than one way. With a single group the count
                           * is the item's own, already on the line above.
                           */}
                          {groups.length > 1 ? <S.GroupQty>{`× ${group?.quantity ?? 1}`}</S.GroupQty> : <></>}
                          <S.Options>
                            {(group?.optionItems || []).map((option: any, optionIndex: number) => (
                              <S.Option key={optionIndex}>
                                <S.OptionName>{option?.optionTitle}</S.OptionName>
                                <S.OptionValue>{optionValue(option)}</S.OptionValue>
                              </S.Option>
                            ))}
                          </S.Options>
                        </S.Group>
                      ))}
                    </S.Groups>
                  ) : (
                    <></>
                  )}
                </S.Line>
              );
            })}
          </S.Lines>
        </PanelBoxLayout>
      )}
    </S.Container>
  );
};

export default CafeteriaOrderDetail;
